import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Card } from '@components/atoms/Card';
import { Accordion } from '@components/atoms/Accordion';
import { Typography, TypographyAppearanceVariants } from '@components/atoms/Typography';
import { OrderSummary, OrderSummaryProps } from './OrderSummary/OrderSummary';
import { OrderPoints } from './OrderPoints/OrderPoints';
import { formatPrice } from '@utils/format';
import styles from './ShoppingCartTotal.module.css';
import { useMediaQuery } from '@hooks/useMediaQuery';

export interface ShoppingCartTotalProps {
    showPointsBelowTotalOnMobile?: boolean; // used on shopping cart page 1
    productsValue: number;
    discount: number;
    eligibleItems: string[];
    subtotal: number;
    shipping: number | null;
    total: number;
    points: number;
    products: OrderSummaryProps['products'];
    nProducts: number;
}

export const ShoppingCartTotal: FC<ShoppingCartTotalProps> = ({
    showPointsBelowTotalOnMobile,
    productsValue,
    discount,
    eligibleItems,
    subtotal,
    shipping,
    total,
    points,
    products,
    nProducts,
}) => {
    const pageSize = useMediaQuery();
    const { t } = useTranslation();

    const translations = {
        summaryLabel: t('shopping-cart-total-component.summary.label'),
        productsAmount: t('shopping-cart-total-component.products.amount', { count: nProducts }),
        taxesInfo: t('shopping-cart-total-component.taxes-info'),
        totalLabel: t('shopping-cart-total-component.total.label'),
    };

    return (
        <Card
            classes={`${styles['shopping-cart-total-card']} ${['xs', 'sm', 'md'].includes(pageSize) && !showPointsBelowTotalOnMobile ? 'border border-0' : 'border'}`}>
            {products.length === 0 ? (
                <OrderSummary
                    showPointsBelowTotalOnMobile={showPointsBelowTotalOnMobile}
                    productsValue={productsValue}
                    discount={discount}
                    eligibleItems={eligibleItems}
                    subtotal={subtotal}
                    shipping={shipping}
                    points={points}
                    products={products}
                />
            ) : (
                <>
                    {/* product summary - visible in big screens only */}
                    <div className="d-none d-lg-block">
                        {products.length > 0 && (
                            <Typography
                                appearanceLevel={TypographyAppearanceVariants.level8}
                                classes={`${styles.indent} pt-3 mb-0`}>
                                {translations.summaryLabel}
                            </Typography>
                        )}
                        <OrderSummary
                            showPointsBelowTotalOnMobile={showPointsBelowTotalOnMobile}
                            productsValue={productsValue}
                            discount={discount}
                            eligibleItems={eligibleItems}
                            subtotal={subtotal}
                            shipping={shipping}
                            points={points}
                            products={products}
                        />
                    </div>
                    {/* product summary accordion - visible in small screens only */}
                    <Accordion
                        id="product-summary"
                        classes="d-lg-none"
                        buttonClasses={`${styles.indent} border-bottom pt-2 pb-2`}
                        bodyClasses="p-0"
                        arrowOnClosed="up"
                        items={[
                            {
                                id: 'collapseProductSummary',
                                buttonContent: (
                                    <Typography appearanceLevel={TypographyAppearanceVariants.level8} classes="mb-0">
                                        {translations.summaryLabel}
                                    </Typography>
                                ),
                                bodyContent: (
                                    <OrderSummary
                                        showPointsBelowTotalOnMobile={showPointsBelowTotalOnMobile}
                                        productsValue={productsValue}
                                        discount={discount}
                                        eligibleItems={eligibleItems}
                                        subtotal={subtotal}
                                        shipping={shipping}
                                        points={points}
                                        products={products}
                                    />
                                ),
                            },
                        ]}
                    />
                </>
            )}

            {/* total value */}
            <div
                className={`d-flex ${showPointsBelowTotalOnMobile && points !== 0 ? 'border-bottom' : ''} pt-2 pt-lg-0 pb-3 ${styles.indent}`}
                data-testid="total">
                <div className="align-content-end">
                    <Typography classes="mb-0" appearanceLevel={TypographyAppearanceVariants.level8}>
                        {translations.totalLabel}
                    </Typography>
                    <Typography classes="mb-0" fontSize="12" color="muted">
                        {translations.productsAmount}
                    </Typography>
                </div>
                <div className="text-end flex-grow-1">
                    <Typography classes="mb-0" fontSize="24" color="primary" fontWeight="800">
                        {formatPrice(total)}
                    </Typography>
                    <Typography classes="mb-0" fontSize="12" color="muted">
                        {translations.taxesInfo}
                    </Typography>
                </div>
            </div>

            <OrderPoints classes={showPointsBelowTotalOnMobile ? 'mb-2' : 'd-none d-lg-block border-top mb-2'} points={points} />
        </Card>
    );
};
